import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import API_URL from '../api';
import Layout from '../components/Layout';

export default function Settings() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  const [form, setForm] = useState({ company_name: '', phone: '', address: '', brand_color: '#2E4057', logo_url: '' });
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  // Team state
  const [users, setUsers] = useState([]);
  const [showUserForm, setShowUserForm] = useState(false);
  const [userForm, setUserForm] = useState({ name: '', email: '', password: '', role: 'staff' });
  const [userMsg, setUserMsg] = useState('');
  const [userErr, setUserErr] = useState('');

  useEffect(() => { loadSettings(); loadUsers(); }, []);

  const loadSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/settings`);
      setForm({
        company_name: res.data.company_name || '',
        phone: res.data.phone || '',
        address: res.data.address || '',
        brand_color: res.data.brand_color || '#2E4057',
        logo_url: res.data.logo_url || ''
      });
    } catch (err) { console.log(err); }
    setLoading(false);
  };

  const loadUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/auth/users`, { headers });
      setUsers(res.data);
    } catch (err) { /* non-admins or errors: show nothing */ }
  };

  const save = async () => {
    try {
      await axios.put(`${API_URL}/api/settings`, form, { headers });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) { alert('Save failed: ' + (err.response?.data?.error || err.message)); }
  };

  const createUser = async (e) => {
    e.preventDefault();
    setUserErr(''); setUserMsg('');
    try {
      await axios.post(`${API_URL}/api/auth/users`, userForm, { headers });
      setUserMsg(`Login created for ${userForm.name}. Share the temporary password with them.`);
      setUserForm({ name: '', email: '', password: '', role: 'staff' });
      setShowUserForm(false);
      loadUsers();
      setTimeout(() => setUserMsg(''), 6000);
    } catch (err) {
      setUserErr(err.response?.data?.error || err.message);
    }
  };

  const toggleActive = async (u) => {
    const verb = u.is_active === false ? 'reactivate' : 'deactivate';
    if (!window.confirm(`Are you sure you want to ${verb} ${u.name || u.email}?`)) return;
    try {
      await axios.put(`${API_URL}/api/auth/users/${u.id}/active`, { is_active: !(u.is_active !== false) }, { headers });
      loadUsers();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
  };

  const resetPassword = async (u) => {
    const newPw = window.prompt(`Enter a new temporary password for ${u.name || u.email}:`);
    if (!newPw) return;
    try {
      await axios.put(`${API_URL}/api/auth/users/${u.id}/password`, { password: newPw }, { headers });
      setUserMsg(`Password reset for ${u.name || u.email}. Share the new password with them.`);
      setTimeout(() => setUserMsg(''), 6000);
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
  };

  const roleLabel = (r) => r === 'admin' ? 'Admin' : r === 'accountant' ? 'Accountant' : 'Staff';

  if (loading) return <div style={{textAlign:'center',padding:'60px'}}>Loading...</div>;

  return (
    <Layout>
      <div style={styles.topbar}>
        <h1 style={styles.pageTitle}>Company Settings</h1>
      </div>

      <div style={styles.content}>
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Branding</h3>
          <p style={styles.helpText}>This information appears on your customer portal and exports.</p>

          <label style={styles.label}>Company Name</label>
          <input style={styles.input} value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} placeholder="Your Company Name" />

          <label style={styles.label}>Phone</label>
          <input style={styles.input} value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="(801) 555-0100" />

          <label style={styles.label}>Address</label>
          <input style={styles.input} value={form.address} onChange={e => setForm({...form, address: e.target.value})} placeholder="123 Main St, City, UT" />

          <label style={styles.label}>Brand Color</label>
          <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
            <input type="color" value={form.brand_color} onChange={e => setForm({...form, brand_color: e.target.value})} style={{width:'48px',height:'40px',border:'1px solid #ddd',borderRadius:'6px',cursor:'pointer'}} />
            <input style={{...styles.input, marginBottom:0, flex:1}} value={form.brand_color} onChange={e => setForm({...form, brand_color: e.target.value})} placeholder="#2E4057" />
          </div>

          <label style={{...styles.label, marginTop:'16px'}}>Logo URL</label>
          <input style={styles.input} value={form.logo_url} onChange={e => setForm({...form, logo_url: e.target.value})} placeholder="https://yoursite.com/logo.png" />
          <p style={styles.helpText}>Paste a link to your logo image. (File upload coming soon.)</p>
          {form.logo_url ? <img src={form.logo_url} alt="Logo preview" style={{maxHeight:'70px',marginTop:'8px',border:'1px solid #eee',borderRadius:'6px',padding:'6px',background:'#fff'}} onError={e => e.target.style.display='none'} /> : null}

          <div style={{marginTop:'24px',display:'flex',alignItems:'center',gap:'14px'}}>
            <button onClick={save} style={styles.btnPrimary}>Save Settings</button>
            {saved ? <span style={{color:'#1E7145',fontWeight:'bold',fontSize:'14px'}}>✓ Saved</span> : null}
          </div>
        </div>

        {/* TEAM ACCESS */}
        <div style={{...styles.card, marginTop:'24px'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <h3 style={styles.cardTitle}>Team Access</h3>
            <button onClick={() => { setShowUserForm(!showUserForm); setUserErr(''); }} style={styles.btnPrimary}>{showUserForm ? 'Cancel' : '+ Add User'}</button>
          </div>
          <p style={styles.helpText}>Give your team members their own logins. They'll see the same company data you do.</p>

          {userMsg ? <div style={styles.successBox}>{userMsg}</div> : null}

          {showUserForm && (
            <form onSubmit={createUser} style={styles.userForm}>
              {userErr ? <div style={styles.errorBox}>{userErr}</div> : null}
              <label style={styles.label}>Full Name</label>
              <input style={styles.input} value={userForm.name} onChange={e => setUserForm({...userForm, name: e.target.value})} placeholder="Cherise Smith" required />

              <label style={styles.label}>Email</label>
              <input style={styles.input} type="email" value={userForm.email} onChange={e => setUserForm({...userForm, email: e.target.value})} placeholder="cherise@company.com" required />

              <label style={styles.label}>Temporary Password</label>
              <input style={styles.input} value={userForm.password} onChange={e => setUserForm({...userForm, password: e.target.value})} placeholder="Give them this to log in" required />

              <label style={styles.label}>Role</label>
              <select style={styles.input} value={userForm.role} onChange={e => setUserForm({...userForm, role: e.target.value})}>
                <option value="staff">Staff — can view and edit jobs</option>
                <option value="accountant">Accountant — can view and edit jobs</option>
                <option value="admin">Admin — full access, can manage users</option>
              </select>

              <button type="submit" style={{...styles.btnPrimary, marginTop:'8px'}}>Create Login</button>
            </form>
          )}

          <table style={styles.table}>
            <thead><tr><th style={styles.th}>Name</th><th style={styles.th}>Email</th><th style={styles.th}>Role</th><th style={styles.th}>Status</th><th style={styles.th}>Actions</th></tr></thead>
            <tbody>
              {users.map(u => {
                const isMe = u.id === currentUser.id;
                const active = u.is_active !== false;
                return (
                  <tr key={u.id} style={{opacity: active ? 1 : 0.5}}>
                    <td style={styles.td}>{u.name || '—'}{isMe ? ' (you)' : ''}</td>
                    <td style={styles.td}>{u.email}</td>
                    <td style={styles.td}>{roleLabel(u.role)}</td>
                    <td style={styles.td}>
                      <span style={{...styles.badge, background: active ? '#E8F5E9' : '#FDEDEC', color: active ? '#1E7145' : '#C0392B'}}>
                        {active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {isMe ? <span style={{color:'#aaa',fontSize:'12px'}}>—</span> : (
                        <div style={{display:'flex',gap:'8px'}}>
                          <button onClick={() => resetPassword(u)} style={styles.btnTiny}>Reset PW</button>
                          <button onClick={() => toggleActive(u)} style={{...styles.btnTiny, color: active ? '#C0392B' : '#1E7145', borderColor: active ? '#C0392B' : '#1E7145'}}>
                            {active ? 'Deactivate' : 'Reactivate'}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 ? <tr><td style={styles.td} colSpan="5">No team members yet.</td></tr> : null}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}

const styles = {
  topbar: { background: '#fff', padding: '20px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e6e9ed' },
  pageTitle: { color: '#2E4057', margin: 0, fontSize: '22px' },
  content: { padding: '32px', maxWidth: '760px' },
  card: { background: '#fff', borderRadius: '10px', padding: '28px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  cardTitle: { margin: '0 0 4px', color: '#2E4057', fontSize: '18px' },
  helpText: { fontSize: '13px', color: '#888', margin: '4px 0 16px' },
  label: { display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', marginBottom: '16px', boxSizing: 'border-box' },
  btnPrimary: { padding: '10px 22px', background: '#2E4057', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' },
  btnTiny: { padding: '5px 10px', background: '#fff', color: '#2E4057', border: '1px solid #2E4057', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' },
  userForm: { background: '#f9f9f9', borderRadius: '8px', padding: '20px', marginBottom: '20px' },
  successBox: { background: '#E8F5E9', color: '#1E7145', padding: '12px', borderRadius: '6px', fontSize: '14px', marginBottom: '16px' },
  errorBox: { background: '#FDEDEC', color: '#C0392B', padding: '12px', borderRadius: '6px', fontSize: '14px', marginBottom: '16px' },
  table: { width: '100%', borderCollapse: 'collapse', marginTop: '8px' },
  th: { padding: '10px 12px', textAlign: 'left', fontSize: '11px', color: '#888', textTransform: 'uppercase', background: '#f9f9f9', letterSpacing: '0.5px' },
  td: { padding: '10px 12px', fontSize: '14px', borderBottom: '1px solid #f0f0f0', verticalAlign: 'middle' },
  badge: { padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold' },
};
